// src/pages/AdminConfirmDocuments.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDocuments, allDocumentTypes } from "../contexts/DocumentsContext";
import AdminConfirmDocumentsHeader from "../components/ui/AdminConfirmDocumentsHeader";
import SpinnerOverlay from "../components/ui/SpinnerOverlay";

/* ===================== Helpers ===================== */
const cls = (...a) => a.filter(Boolean).join(" ");

/* ===================== Icons ===================== */
const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const RefreshIcon = ({ spinning }) => (
  <svg className={`w-4 h-4 ${spinning ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const DocumentIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const EditIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const UserIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const MailIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const CalendarIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ClockIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const SendIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

/* ===================== UI Components ===================== */
const getStatusInfo = (status) => {
  const statusConfig = {
    NO_RESPONSE:    { label: "ממתין",       color: "from-amber-500 to-orange-500",  bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200"   },
    NEEDS_REVISION: { label: "דורש תיקון", color: "from-purple-500 to-pink-500",   bg: "bg-purple-50",  text: "text-purple-700",  border: "border-purple-200"  },
    APPROVED:       { label: "מאושר",       color: "from-emerald-500 to-green-500", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
    REJECTED:       { label: "נדחה",        color: "from-red-500 to-rose-500",      bg: "bg-red-50",     text: "text-red-700",     border: "border-red-200"     },
  };
  return statusConfig[status] || statusConfig.NO_RESPONSE;
};

const StatusBadge = ({ status }) => {
  const info = getStatusInfo(status);
  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold ${info.bg} ${info.text} border ${info.border}`}>
      {info.label}
    </span>
  );
};

/* ===================== Extra fields config ===================== */
const EXTRA_FIELDS_BY_TYPE = {
  "תעודת זהות": [
    {
      key: "idNumber",
      label: "מספר תעודת זהות",
      placeholder: "לדוגמה: 123456789",
      required: true,
      type: "text",
      validation: Yup.string().trim().matches(/^\d{9}$/, "מספר תעודת זהות חייב להיות 9 ספרות בדיוק").required("שדה חובה: מספר תעודת זהות"),
    },
  ],
  "תיאום מס": [
    {
      key: "taxPercent",
      label: "אחוז מס (מס הכנסה)",
      placeholder: "לדוגמה: 10",
      required: true,
      type: "number",
      validation: Yup.number().typeError("חובה להזין מספר").transform((v, orig) => (orig === "" || orig === null ? undefined : v)).min(0, "מינימום 0").max(100, "מקסימום 100").required("שדה חובה: אחוז מס"),
    },
    {
      key: "taxValidTo",
      label: "תוקף עד (תאריך פקיעה)",
      placeholder: "",
      required: true,
      type: "date",
      validation: Yup.string().trim().matches(/^\d{4}-\d{2}-\d{2}$/, "פורמט תאריך לא תקין (YYYY-MM-DD)").required("שדה חובה: תוקף עד"),
    },
  ],
  "אישור ניהול חשבון": [
    {
      key: "bankNumber",
      label: "מספר בנק",
      placeholder: "לדוגמה: 20",
      required: true,
      type: "text",
      validation: Yup.string().trim().matches(/^\d{1,3}$/, "מספר בנק חייב להיות ספרות בלבד (1-3 ספרות)").required("שדה חובה: מספר בנק"),
    },
    {
      key: "branch",
      label: "סניף",
      placeholder: "לדוגמה: 123",
      required: true,
      type: "text",
      validation: Yup.string().trim().matches(/^\d{1,6}$/, "סניף חייב להיות ספרות בלבד (עד 6 ספרות)").required("שדה חובה: סניף"),
    },
    {
      key: "account",
      label: "מספר חשבון",
      placeholder: "לדוגמה: 456789",
      required: true,
      type: "text",
      validation: Yup.string().trim().matches(/^\d{4,20}$/, "מספר חשבון חייב להיות ספרות בלבד (4-20 ספרות)").required("שדה חובה: מספר חשבון"),
    },
  ],
  // Supplier equivalent of "תיאום מס" — same documents-table columns
  // (tax_percent/tax_valid_from/tax_valid_to), but the certificate
  // itself states both ends of the validity period explicitly (see
  // the sample cert), so both are entered here rather than deriving
  // taxValidFrom from the upload date the way the teacher version does.
  "אישור ניכוי מס במקור": [
    {
      key: "taxPercent",
      label: "אחוז ניכוי",
      placeholder: "לדוגמה: 0 (פטור מלא)",
      required: true,
      type: "number",
      validation: Yup.number().typeError("חובה להזין מספר").transform((v, orig) => (orig === "" || orig === null ? undefined : v)).min(0, "מינימום 0").max(100, "מקסימום 100").required("שדה חובה: אחוז ניכוי"),
    },
    {
      key: "taxValidFrom",
      label: "תוקף מיום",
      placeholder: "",
      required: true,
      type: "date",
      validation: Yup.string().trim().matches(/^\d{4}-\d{2}-\d{2}$/, "פורמט תאריך לא תקין (YYYY-MM-DD)").required("שדה חובה: תוקף מיום"),
    },
    {
      key: "taxValidTo",
      label: "תוקף עד",
      placeholder: "",
      required: true,
      type: "date",
      validation: Yup.string().trim().matches(/^\d{4}-\d{2}-\d{2}$/, "פורמט תאריך לא תקין (YYYY-MM-DD)").required("שדה חובה: תוקף עד"),
    },
  ],
  // Business registration — writes to supplier_profiles, not users,
  // since business_number/address already live there (set at signup
  // as placeholders, confirmed for real here on approval).
  "תעודת עוסק מורשה": [
    {
      key: "businessNumber",
      label: "מספר עוסק מורשה",
      placeholder: "לדוגמה: 12345678",
      required: true,
      type: "text",
      validation: Yup.string().trim().matches(/^\d{7,9}$/, "מספר עוסק חייב להיות 7-9 ספרות").required("שדה חובה: מספר עוסק"),
    },
    {
      key: "businessAddress",
      label: "כתובת העסק (לפי התעודה)",
      placeholder: "לדוגמה: התקווה 3, ירושלים",
      required: false,
      type: "text",
      validation: Yup.string().trim(),
    },
  ],
};

const statusOptions = ["NO_RESPONSE", "NEEDS_REVISION", "APPROVED", "REJECTED"];

/* ===================== Yup schema builder ===================== */
const buildAdminDocSchema = (requiredExtraFields) => {
  const extraShape = {};
  (requiredExtraFields || []).forEach((f) => {
    extraShape[f.key] = f.validation || Yup.string().trim().required(`שדה חובה: ${f.label}`);
  });

  return Yup.object({
    actionStatus: Yup.string().oneOf(["APPROVED", "REJECTED", "NEEDS_REVISION"], "נא לבחור פעולה").required("נא לבחור פעולה"),
    notes: Yup.string().when("actionStatus", {
      is: (v) => v === "REJECTED" || v === "NEEDS_REVISION",
      then: (s) => s.trim().required("בעת דחייה/תיקון חובה לרשום הערה."),
      otherwise: (s) => s.notRequired(),
    }),
    extra: Yup.object().when("actionStatus", {
      is: "APPROVED",
      then: () => Yup.object(extraShape),
      otherwise: () => Yup.object().strip(),
    }),
  });
};

/* ===================== Page ===================== */
export default function AdminConfirmDocuments() {
  const {
    adminDocuments,
    loadAdminDocuments,
    adminUpdateDocument,
    adminFilters,
    setAdminFilters,
    isLoading,
    adminPage,
    setAdminPage,
    adminLimit,
    adminTotal,
    adminCounts,
    loadAdminCounts,
  } = useDocuments();

  const docs = Array.isArray(adminDocuments) ? adminDocuments : [];

  const [selected,           setSelected]           = useState(null);
  const [refreshing,         setRefreshing]         = useState(false);
  const [submitting,         setSubmitting]         = useState(false);
  const [showInlinePreview,  setShowInlinePreview]  = useState(true);

  // Used for mobile scroll-to-details
  const detailsPanelRef = useRef(null);

  const requiredExtraFields = useMemo(() => {
    const arr = EXTRA_FIELDS_BY_TYPE[selected?.type];
    return Array.isArray(arr) ? arr : [];
  }, [selected]);

  const extraInitialValues = useMemo(() => {
    const obj = {};
    (requiredExtraFields || []).forEach((f) => (obj[f.key] = ""));
    return obj;
  }, [requiredExtraFields]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      actionStatus: "",
      notes: selected?.notes || "",
      extra: extraInitialValues,
    },
    validationSchema: buildAdminDocSchema(requiredExtraFields),
    onSubmit: async (values) => {
      if (!selected?.id) return;
      setSubmitting(true);
      try {
        formik.setStatus("");

        const payload = { status: values.actionStatus, notes: values.notes || "" };
        if (values.actionStatus === "APPROVED" && requiredExtraFields.length > 0) {
          payload.extra = values.extra || {};
        }

        await adminUpdateDocument(selected.id, payload);
        await loadAdminDocuments(adminFilters, adminPage, adminLimit);
        await loadAdminCounts();

        setSelected(null);
        formik.resetForm();
      } catch (e) {
        formik.setStatus(e?.message || "שגיאה בעת עדכון מסמך");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const docAvatarGradient = (status) => `bg-gradient-to-br ${getStatusInfo(status).color}`;

  const docInitials = (type) => {
    const chars = String(type || "").trim().replace(/[^0-9A-Za-zא-ת]/g, "");
    const out = chars.slice(0, 2);
    return out ? out.toUpperCase() : "D";
  };

  const totalPages = useMemo(() => {
    const t = Number(adminTotal || 0);
    const l = Number(adminLimit || 4);
    return Math.max(1, Math.ceil(t / l));
  }, [adminTotal, adminLimit]);

  const counts = useMemo(() => ({
    waiting:  Number(adminCounts?.NO_RESPONSE    || 0),
    approved: Number(adminCounts?.APPROVED       || 0),
    revision: Number(adminCounts?.NEEDS_REVISION || 0),
    rejected: Number(adminCounts?.REJECTED       || 0),
    total:    Number(adminCounts?.TOTAL          || 0),
  }), [adminCounts]);

  // Removed `formik` from deps — it's unstable across renders and the
  // resetForm call captures the latest value via closure at call time.
  const handleSelectDocument = useCallback((doc) => {
    setSelected(doc);
    setShowInlinePreview(true);

    const initExtra = {};
    (EXTRA_FIELDS_BY_TYPE[doc?.type] || []).forEach((f) => (initExtra[f.key] = ""));

    formik.resetForm({ values: { actionStatus: "", notes: doc?.notes || "", extra: initExtra } });
    formik.setStatus("");

    // On mobile/tablet, scroll the details panel into view
    setTimeout(() => {
      detailsPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAdminDocuments(adminFilters, adminPage, adminLimit);
    await loadAdminCounts();
    setTimeout(() => setRefreshing(false), 400);
  };

  const openPdfNewTab = useCallback((url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  useEffect(() => {
    loadAdminCounts();
    loadAdminDocuments(adminFilters, adminPage, adminLimit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAdminPage(1);
    const t = setTimeout(() => { loadAdminDocuments(adminFilters, 1, adminLimit); }, 250);
    return () => clearTimeout(t);
  }, [adminFilters, adminLimit, loadAdminDocuments, setAdminPage]);

  useEffect(() => {
    loadAdminDocuments(adminFilters, adminPage, adminLimit);
  }, [adminPage, adminLimit, adminFilters, loadAdminDocuments]);

  return (
    <>
      {submitting && <SpinnerOverlay variant="glow" text="שומר החלטה..." dark={false} />}
      <AdminConfirmDocumentsHeader />

      <div className="min-h-screen max-w-7xl mx-auto p-4 sm:p-6" dir="rtl">

        {/* ── Filters ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 md:p-8 mb-6">
          {/*
            On mobile: all items stack in a column.
            On sm+: row layout. The two selects and refresh button are
            auto-width; search fills the remaining space.
          */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-3">

            {/* Search — full-width on xs, grows to fill on sm+ */}
            <div className="flex-1 min-w-0 sm:min-w-[200px]">
              <div className="relative">
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <SearchIcon />
                </span>
                <input
                  value={adminFilters?.search || ""}
                  onChange={(e) => setAdminFilters((p) => ({ ...(p || {}), search: e.target.value }))}
                  placeholder="חיפוש מסמך, משתמש..."
                  className="w-full px-4 pr-10 py-2 rounded-lg border border-gray-200 bg-white outline-none transition-all focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />
              </div>
            </div>

            {/* Status select */}
            <select
              value={adminFilters?.status || "NO_RESPONSE"}
              onChange={(e) => setAdminFilters((p) => ({ ...(p || {}), status: e.target.value }))}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-200 bg-white outline-none transition-all focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 font-semibold text-gray-700"
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>{getStatusInfo(s).label}</option>
              ))}
            </select>

            {/* Type select */}
            <select
              value={adminFilters?.type || ""}
              onChange={(e) => setAdminFilters((p) => ({ ...(p || {}), type: e.target.value }))}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-200 bg-white outline-none transition-all focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 font-semibold text-gray-700"
            >
              <option value="">כל הסוגים</option>
              {allDocumentTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            {/* Refresh */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-semibold bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              <RefreshIcon spinning={refreshing} />
              {refreshing ? "מרענן..." : "רענן"}
            </button>
          </div>
        </div>

        {/* ── KPI Cards ── */}
        {/* 2 cols on xs, 4 cols on sm+ */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-3 sm:p-4 rounded-xl border border-amber-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                <ClockIcon className="text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-amber-700">ממתינים</p>
                <p className="text-xl sm:text-2xl font-bold text-amber-900">{counts.waiting}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-3 sm:p-4 rounded-xl border border-emerald-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                <CheckIcon className="text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-emerald-700">מאושרים</p>
                <p className="text-xl sm:text-2xl font-bold text-emerald-900">{counts.approved}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 sm:p-4 rounded-xl border border-purple-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                <EditIcon className="text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-purple-700">דורשים תיקון</p>
                <p className="text-xl sm:text-2xl font-bold text-purple-900">{counts.revision}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-50 to-rose-50 p-3 sm:p-4 rounded-xl border border-red-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-red-500 to-rose-500 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                <XIcon className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-red-700">נדחו</p>
                <p className="text-xl sm:text-2xl font-bold text-red-900">{counts.rejected}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Main two-panel grid ──
            Stacks on mobile (list first, details below), side-by-side at lg.
        */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Documents List ── */}
          <div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Panel header */}
              <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <h2 className="text-base font-bold text-gray-900">רשימת מסמכים</h2>
                  <div className="text-sm text-gray-600 font-medium">
                    עמוד {adminPage} מתוך {totalPages} • {docs.length} מסמכים
                  </div>
                </div>
              </div>

              {/* List body — shorter on mobile to leave room for the details panel */}
              <div className="max-h-[360px] sm:max-h-[480px] lg:max-h-[620px] overflow-y-auto divide-y divide-gray-100">
                {docs.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                      <DocumentIcon className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-500" />
                    </div>
                    <p className="text-gray-800 font-bold text-base sm:text-lg">
                      {isLoading ? "טוען מסמכים..." : "אין מסמכים להצגה"}
                    </p>
                    <p className="text-gray-500 text-sm mt-2">נסה לשנות את הסינון או החיפוש</p>
                  </div>
                ) : (
                  docs.map((doc) => {
                    const isActive = selected?.id === doc.id;
                    const waiting  = doc.status === "NO_RESPONSE";

                    return (
                      <div
                        key={doc.id}
                        className={cls(
                          "cursor-pointer transition-colors",
                          isActive ? "bg-indigo-50/60 hover:bg-indigo-50/80" : "hover:bg-gray-50"
                        )}
                        onClick={() => handleSelectDocument(doc)}
                      >
                        <div className="p-4 sm:p-5">
                          <div className="flex items-start gap-3 sm:gap-4">
                            {/* Avatar */}
                            <div className="flex-shrink-0">
                              <div className={cls("w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center font-bold text-white shadow-sm", docAvatarGradient(doc.status))}>
                                <span className="text-base sm:text-lg">{docInitials(doc.type)}</span>
                              </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1.5">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className={cls("font-bold text-sm sm:text-base", isActive ? "text-indigo-900" : "text-gray-900")}>
                                      {doc.type || "מסמך"}
                                    </span>
                                    {waiting && (
                                      <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-bold">חדש</span>
                                    )}
                                  </div>

                                  <p className="text-xs sm:text-sm text-gray-600 font-medium line-clamp-1 mb-1.5">
                                    {doc.customName || doc.fileName}
                                  </p>

                                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap text-xs sm:text-sm">
                                    <span className="font-semibold text-gray-700">
                                      {doc.user?.firstName} {doc.user?.lastName}
                                    </span>
                                    <span className="text-gray-300">•</span>
                                    <span className="text-gray-500 truncate max-w-[140px] sm:max-w-none">
                                      {doc.user?.email}
                                    </span>
                                    {doc.date && (
                                      <>
                                        <span className="text-gray-300">•</span>
                                        <span className="text-xs text-gray-500 font-medium">{doc.date}</span>
                                      </>
                                    )}
                                  </div>
                                </div>

                                <div className="flex-shrink-0">
                                  <StatusBadge status={doc.status} />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Pagination */}
              <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200">
                <div className="flex items-center justify-between gap-2">
                  <button
                    disabled={adminPage >= totalPages}
                    onClick={() => setAdminPage((p) => Math.min(totalPages, p + 1))}
                    className={cls(
                      "px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                      adminPage >= totalPages
                        ? "bg-white border border-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                    )}
                  >
                   → הבא 
                  </button>

                  <div className="text-sm text-gray-700 font-semibold">
                    <span className="text-gray-900">{adminPage}</span>
                    <span className="text-gray-400 mx-1">/</span>
                    <span className="text-gray-900">{totalPages}</span>
                  </div>

                  <button
                    disabled={adminPage <= 1}
                    onClick={() => setAdminPage((p) => Math.max(1, p - 1))}
                    className={cls(
                      "px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                      adminPage <= 1
                        ? "bg-white border border-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                    )}
                  >
                     הקודם ←
                  </button>

                </div>
              </div>
            </div>
          </div>

          {/* ── Document Details Panel ── */}
          <div ref={detailsPanelRef}>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm overflow-hidden lg:sticky lg:top-6">
              {!selected ? (
                <div className="p-8">
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                      <DocumentIcon className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-500" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">בחר מסמך לצפייה</h3>
                    <p className="text-gray-600 text-sm">לחץ על מסמך מהרשימה כדי לצפות בפרטים</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Details header */}
                  <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className={cls("w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg flex-shrink-0", docAvatarGradient(selected.status))}>
                          {docInitials(selected.type)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{selected.type}</h3>
                          {selected.customName && (
                            <p className="text-xs sm:text-sm text-gray-600 truncate">{selected.customName}</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelected(null)}
                        className="p-2 rounded-xl hover:bg-white/80 border border-indigo-200 transition-colors flex-shrink-0"
                        title="סגור"
                      >
                        <XIcon />
                      </button>
                    </div>
                  </div>

                  {/* User info */}
                  <div className="p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-br from-gray-50 to-white">
                    <h4 className="text-sm font-bold text-gray-900 mb-3">פרטי משתמש</h4>
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <UserIcon className="text-indigo-600" />
                        </div>
                        <span className="font-semibold text-gray-900 text-sm">
                          {selected.user?.firstName} {selected.user?.lastName}
                        </span>
                      </div>

                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <MailIcon className="text-purple-600" />
                        </div>
                        <span className="text-gray-700 text-sm truncate">{selected.user?.email}</span>
                      </div>

                      {selected.date && (
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <CalendarIcon className="text-pink-600" />
                          </div>
                          <span className="text-gray-700 text-sm">{selected.date}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* PDF actions + preview */}
                  <div className="p-4 sm:p-6 border-b border-gray-200 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge status={selected.status} />

                      <button
                        onClick={() => openPdfNewTab(selected.url)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-gray-800 border border-gray-300 text-sm font-semibold hover:bg-gray-50 transition-all"
                      >
                        <ExternalLinkIcon />
                        <span>פתח PDF</span>
                      </button>

                      <button
                        onClick={() => setShowInlinePreview((v) => !v)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-gray-700 border border-gray-300 text-sm font-semibold hover:bg-gray-50 transition-all"
                      >
                        {showInlinePreview ? "הסתר" : "הצג"}
                      </button>
                    </div>

                    {/* PDF iframe — shorter on mobile, taller on lg+ */}
                    {showInlinePreview && (
                      <div className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50">
                        <iframe
                          title="PDF Preview"
                          src={selected.url}
                          className="w-full h-[280px] sm:h-[360px] lg:h-[400px]"
                        />
                      </div>
                    )}
                  </div>

                  {/* Decision form */}
                  <form onSubmit={formik.handleSubmit} className="p-4 sm:p-6 space-y-4">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-3">בחר פעולה</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => formik.setFieldValue("actionStatus", "APPROVED")}
                          className={cls(
                            "p-2.5 sm:p-3 rounded-lg border-2 text-sm font-semibold transition-all flex items-center justify-center gap-2",
                            formik.values.actionStatus === "APPROVED"
                              ? "bg-gradient-to-r from-emerald-600 to-green-600 text-white border-emerald-600 shadow-sm"
                              : "border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 text-gray-700"
                          )}
                        >
                          <CheckIcon />
                          <span>אשר</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => formik.setFieldValue("actionStatus", "REJECTED")}
                          className={cls(
                            "p-2.5 sm:p-3 rounded-lg border-2 text-sm font-semibold transition-all flex items-center justify-center gap-2",
                            formik.values.actionStatus === "REJECTED"
                              ? "bg-gradient-to-r from-red-600 to-rose-600 text-white border-red-600 shadow-sm"
                              : "border-gray-200 hover:border-red-300 hover:bg-red-50 text-gray-700"
                          )}
                        >
                          <XIcon className="w-4 h-4" />
                          <span>דחה</span>
                        </button>
                      </div>

                      {formik.touched.actionStatus && formik.errors.actionStatus && (
                        <div className="mt-2 text-sm text-red-600 font-semibold">{formik.errors.actionStatus}</div>
                      )}
                    </div>

                    {/* Extra fields — only when APPROVED and doc type requires them */}
                    {formik.values.actionStatus === "APPROVED" && requiredExtraFields.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-sm font-bold text-gray-900">פרטים נוספים לאישור</h4>
                        <div className="bg-indigo-50/50 rounded-xl p-4 border border-indigo-200 space-y-3">
                          {requiredExtraFields.map((field) => {
                            const name = `extra.${field.key}`;
                            const err  = formik.touched?.extra?.[field.key] && formik.errors?.extra?.[field.key];
                            return (
                              <div key={field.key}>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                  {field.label}
                                  {field.required && <span className="text-red-500 mr-1">*</span>}
                                </label>
                                <input
                                  name={name}
                                  type={field.type || "text"}
                                  value={formik.values.extra?.[field.key] ?? ""}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  placeholder={field.placeholder || ""}
                                  className={cls(
                                    "w-full px-3 py-2 rounded-lg border-2 bg-white outline-none transition-all font-medium focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400",
                                    err ? "border-red-300 bg-red-50" : "border-gray-200"
                                  )}
                                />
                                {err && <div className="mt-1 text-sm text-red-600 font-semibold">{err}</div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        הערות למשתמש
                        {(formik.values.actionStatus === "REJECTED" || formik.values.actionStatus === "NEEDS_REVISION") && (
                          <span className="text-red-500 mr-1">*</span>
                        )}
                      </label>
                      <textarea
                        name="notes"
                        value={formik.values.notes}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="הוסף הערות..."
                        rows={4}
                        className={cls(
                          "w-full px-3 py-2 rounded-lg border-2 outline-none transition-all resize-none font-medium focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400",
                          formik.touched.notes && formik.errors.notes ? "border-red-300 bg-red-50" : "border-gray-200"
                        )}
                      />
                      {formik.touched.notes && formik.errors.notes && (
                        <div className="mt-1 text-sm text-red-600 font-semibold">{formik.errors.notes}</div>
                      )}
                    </div>

                    {formik.status && (
                      <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm font-medium">
                        {formik.status}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting || !formik.values.actionStatus}
                      className="w-full py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center justify-center gap-2"
                    >
                      <SendIcon />
                      <span>{submitting ? "שולח..." : "שמור החלטה"}</span>
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}